/** @format */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { UpdateTaskPayload } from '@clarix/shared-types';
// FLAG (Wave 3 service layer): `buildMovePayload` lives in clario-frontend's
// `src/utils/common-functions/task-project-move.ts` and depends on `tasksApi`.
// It is NOT yet ported into `@clarix/utils`. Wave 3 must port it and re-export
// from `@clarix/utils` (or a dedicated `@clarix/task-utils` package).
import { buildMovePayload } from '@clarix/utils';
import { toast } from '@clarix/ui';
import { getApiErrorMessage } from '@clarix/utils';

// ── Types ─────────────────────────────────────────────────────────────

interface MutateCallbacks {
  onSuccess?: () => void;
  onError?: (err: Error) => void;
  onSettled?: () => void;
}

interface UseProjectMoveScopeOptions {
  /** Whether the modal is open (resets scope when closed) */
  open: boolean;
  /** The task's original project ID (before any move) */
  originalProjectId: string | null;
  /** Current task type name — used for auto-mapping to target scope */
  currentTypeName: string | undefined;
  /** Current task status name — used for auto-mapping to target scope */
  currentStatusName: string | undefined;
  /** The mutation's `mutate` function (from useUpdateTask) */
  mutateFn: (payload: UpdateTaskPayload, options: MutateCallbacks) => void;
  /** Called after a successful move — use for list refreshes, query invalidation, etc. */
  onMoveSuccess?: (targetProjectId: number | null) => void;
  /** Guard: when false, handleProjectChange is a no-op (e.g. task is null) */
  enabled?: boolean;
}

interface UseProjectMoveScopeReturn {
  /** Effective project ID (reflects scope after move) */
  effectiveProjectId: string | null;
  /** Whether the effective scope is a project (vs org-level) */
  hasProject: boolean;
  /** Whether a move is currently in flight */
  isMoving: boolean;
  /** Initiates a project move */
  handleProjectChange: (targetProjectId: number | null) => Promise<void>;
}

// ── Hook ──────────────────────────────────────────────────────────────

export function useProjectMoveScope({
  open,
  originalProjectId,
  currentTypeName,
  currentStatusName,
  mutateFn,
  onMoveSuccess,
  enabled = true,
}: UseProjectMoveScopeOptions): UseProjectMoveScopeReturn {
  const [scopeOverride, setScopeOverride] = useState<string | null | undefined>(undefined);
  const [isMoving, setIsMoving] = useState(false);

  // Reset scope override when modal closes (derived-state pattern).
  const [prevOpen, setPrevOpen] = useState(false);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (!open && scopeOverride !== undefined) {
      setScopeOverride(undefined);
    }
  }

  const effectiveProjectId = scopeOverride === undefined ? originalProjectId : scopeOverride;
  const hasProject = !!effectiveProjectId;

  // Use refs for values that change frequently to keep handleProjectChange stable
  // and avoid stale closures without triggering re-creation of the callback.
  const onMoveSuccessRef = useRef(onMoveSuccess);
  useEffect(() => {
    onMoveSuccessRef.current = onMoveSuccess;
  });

  const currentTypeNameRef = useRef(currentTypeName);
  useEffect(() => {
    currentTypeNameRef.current = currentTypeName;
  });

  const currentStatusNameRef = useRef(currentStatusName);
  useEffect(() => {
    currentStatusNameRef.current = currentStatusName;
  });

  const handleProjectChange = useCallback(
    async (targetProjectId: number | null) => {
      if (!enabled) return;
      setIsMoving(true);
      try {
        const payload = await buildMovePayload(
          targetProjectId,
          currentTypeNameRef.current,
          currentStatusNameRef.current,
        );
        mutateFn(payload, {
          onSuccess: () => {
            toast.success('Task moved successfully');
            setScopeOverride(targetProjectId ? String(targetProjectId) : null);
            onMoveSuccessRef.current?.(targetProjectId);
          },
          onError: (err) => {
            toast.error(getApiErrorMessage(err, 'Failed to move task'));
          },
          onSettled: () => {
            setIsMoving(false);
          },
        });
      } catch {
        setIsMoving(false);
        toast.error('Failed to move task');
      }
    },
    [enabled, mutateFn],
  );

  return { effectiveProjectId, hasProject, isMoving, handleProjectChange };
}
